/** @format */

import { formatDate } from 'date-fns';
import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'src/store';
import { getTrainings, getBookings } from 'src/slices/academy';
import { LocationIcon, SearchIcon } from 'src/components/icons';
import trainingPlaceholder from 'src/assets/imgs/training-placeholder.png';
import useTrainingPreviewImages from 'src/hooks/useTrainingPreviewImages';
import { IS_PRODUCTION } from 'src/constants';

function TrainingPreviewImage({ previewImageStates, trainingId }) {
  const previewState = previewImageStates[trainingId];

  return (
    <div className={`academy-widget--item-image${previewState ? '' : ' academy-widget--item-image--loading'}`}>
      {previewState ? <img src={previewState.url || trainingPlaceholder} alt="" /> : null}
    </div>
  );
}

export function AcademyWidget({ data }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(1);
  const dispatch = useDispatch();
  const { trainings, trainingsLoading, trainingsError } = useSelector(state => state.academy);
  const { bookings, bookingsLoading, bookingsError } = useSelector(state => state.academy);
  const [search, setSearch] = useState('');
  const trainingIds = useMemo(() => trainings?.map(training => training.ID) ?? [], [trainings]);
  const bookingTrainingIds = useMemo(() => bookings?.map(booking => booking.TrainingID) ?? [], [bookings]);
  const trainingPreviewImageStates = useTrainingPreviewImages(trainingIds);
  const bookingPreviewImageStates = useTrainingPreviewImages(bookingTrainingIds);

  const crmUrl = useMemo(() => {
    return IS_PRODUCTION ? 'https://academy.ate-vdo.com' : 'https://uat-continental.crm-e.net';
  }, [IS_PRODUCTION]);

  useEffect(() => {
    dispatch(getTrainings());
    dispatch(getBookings());
  }, [dispatch]);

  const handleSubmit = e => {
    e.preventDefault();
    const url = `${crmUrl}/de/de-DE/GlobalSearch?q=${search}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="academy-widget" data-width={data.w} data-height={data.h}>
      <div className="academy-widget--title">
        <h2>{data.title}</h2>
        <form onSubmit={handleSubmit} className="academy-widget--search-form">
          <input
            type="text"
            name="q"
            placeholder={t('widget.searchTrainings')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button type="submit">
            <SearchIcon />
          </button>
        </form>
      </div>
      <div className="academy-widget--tabs-wrapper">
        <ul className="academy-widget--tabs-tab">
          <li>
            <button className={tab === 1 ? 'active' : ''} onClick={() => setTab(1)}>
              {t('widget.newTrainings')}
            </button>
          </li>
          <li>
            <button className={tab === 2 ? 'active' : ''} onClick={() => setTab(2)}>
              {t('widget.bookings')}
            </button>
          </li>
        </ul>
        <div className="academy-widget--tabs-content">
          {tab === 1 && (
            <div id="new-training" className="academy-widget--list">
              {trainings && trainings.length > 0 ? (
                trainings.map((training, i) => (
                  <a
                    href={`${crmUrl}/de/de-DE/Training/CourseDetails/${training.ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={training.ID + i}
                    className="academy-widget--item">
                    <TrainingPreviewImage previewImageStates={trainingPreviewImageStates} trainingId={training.ID} />
                    <div className="academy-widget--item-info">
                      <div className="academy-widget--item-title">{training.Title}</div>
                      <div className="academy-widget--item-date-location">
                        <div className="academy-widget--item-date">
                          {formatDate(new Date(training.StartDate), 'dd.MM.yyyy')}
                        </div>
                      </div>
                      <div className="academy-widget--item-location">
                        <LocationIcon /> Multiple locations
                      </div>
                      <div className="academy-widget--item-text">{training.LearningTargets}</div>
                    </div>
                  </a>
                ))
              ) : trainings && !trainingsLoading && !trainingsError ? (
                <div className="academy-widget--list-empty">{t('widget.trainingNoTrainingAvailable')}</div>
              ) : !trainingsLoading && trainingsError ? (
                <div className="academy-widget--list-empty">{t('widget.trainingDataCouldNotBeLoaded')}</div>
              ) : null}
            </div>
          )}
          {tab === 2 && (
            <div id="booking" className="academy-widget--list">
              {bookings.length ? (
                bookings.map(booking => (
                  <a
                    href={`${crmUrl}/de/de-DE/Training/CourseBooking/${booking.TrainingID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={booking.TrainingID}
                    className="academy-widget--item">
                    <TrainingPreviewImage
                      previewImageStates={bookingPreviewImageStates}
                      trainingId={booking.TrainingID}
                    />
                    <div className="academy-widget--item-info">
                      <div className="academy-widget--item-title">{booking.TrainingTitle}</div>
                      <div className="academy-widget--item-date-location">
                        <div className="academy-widget--item-date">
                          {formatDate(new Date(booking.TrainingStartDate), 'dd.MM.yyyy')}
                        </div>
                      </div>
                      <div className="academy-widget--item-location">
                        <LocationIcon /> {booking.VenueCity || `Online`}
                      </div>
                      <div className="academy-widget--item-text">{booking.LearningTargets}</div>
                    </div>
                  </a>
                ))
              ) : bookings && !bookingsLoading && !bookingsError ? (
                <div className="academy-widget--list-empty">{t('widget.trainingNoTrainingAvailable')}</div>
              ) : !bookingsLoading && bookingsError ? (
                <div className="academy-widget--list-empty">{t('widget.trainingDataCouldNotBeLoaded')}</div>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <div className="academy-widget--link">
        <a href={crmUrl} target="_blank" rel="noopener noreferrer">
          {t('widget.goToTrainingCenter')}
        </a>
      </div>
    </div>
  );
}
